/**
 * Error handling utilities for order change submission
 *
 * These utilities help classify errors and determine appropriate user actions.
 */

import {AxiosError} from 'axios'

import {hubQueryClient} from '../../../../common/react-query/QueryProvider'
import {ErrorModalVariant} from '../types/orderChangeResult.types'

/**
 * HTTP status codes for error classification
 */
const HTTP_STATUS = {
  BAD_REQUEST: 400,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
} as const

/**
 * Error codes returned by the API for specific scenarios
 */
const ERROR_CODES = {
  CHECKSUM_MISMATCH: 'CHECKSUM_MISMATCH',
  ORDER_MODIFIED: 'ORDER_MODIFIED',
  STALE_DATA: 'STALE_DATA'
} as const

/**
 * Response type for HConnect API errors
 */
interface HConnectErrorResponse {
  errorCode?: string
  message?: string
  detail?: string
}

/**
 * Check if error is an Axios error
 */
export const isAxiosError = (error: unknown): error is AxiosError<HConnectErrorResponse> => {
  return error instanceof Error && 'isAxiosError' in error && (error as AxiosError).isAxiosError
}

/**
 * Checks if the error is a checksum validation error.
 * Checksum errors indicate that the order data has changed since it was loaded.
 *
 * @param error - Error from the API request
 * @returns true if the error is a checksum error
 */
export const isCheckSumError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false

  const status = error.response?.status
  const data = error.response?.data
  const errorCode = data?.errorCode
  const errorMessage = data?.message?.toLowerCase() ?? ''
  const errorDetail = data?.detail?.toLowerCase() ?? ''

  // Check for specific checksum error message
  if (errorDetail.includes('checksum is invalid')) {
    return true
  }

  // Check for 400 with checksum-related error codes or messages
  if (status === HTTP_STATUS.BAD_REQUEST) {
    if (
      errorCode === ERROR_CODES.CHECKSUM_MISMATCH ||
      errorCode === ERROR_CODES.ORDER_MODIFIED ||
      errorCode === ERROR_CODES.STALE_DATA
    ) {
      return true
    }

    // Check for checksum-related keywords in error message
    if (
      errorMessage.includes('checksum') ||
      errorMessage.includes('modified') ||
      errorMessage.includes('stale') ||
      errorMessage.includes('conflict')
    ) {
      return true
    }
  }

  return false
}

/**
 * Determines if a retry should be offered to the user based on the error type.
 *
 * Retry is offered for:
 * - Server errors (500-504 status codes)
 * - Network connectivity issues
 *
 * Retry is NOT offered for:
 * - Checksum errors (data has changed)
 * - Client errors (4xx)
 *
 * @param error - Error from the API request
 * @returns true if retry should be offered
 */
export const isRetryableError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false

  const status = error.response?.status

  // Server errors (500-504) are retryable
  if (
    status &&
    status >= HTTP_STATUS.INTERNAL_SERVER_ERROR &&
    status <= HTTP_STATUS.GATEWAY_TIMEOUT
  ) {
    return true
  }

  // Network errors (no response) are retryable
  if (!error.response || error.code === 'ERR_NETWORK') {
    return true
  }

  return false
}

/**
 * Determine the error modal variant based on the error type.
 *
 * @param error - Error from the API request
 * @returns The appropriate error modal variant
 */
export const getErrorModalVariant = (error: unknown): ErrorModalVariant => {
  if (isCheckSumError(error)) {
    return 'checksum'
  }

  if (isRetryableError(error)) {
    return 'retryable'
  }

  return 'nonRetryable'
}

/**
 * Extract a user-friendly error message from an error.
 *
 * @param error - Error from the API request
 * @returns User-friendly error message
 */
export const getErrorMessage = (error: unknown): string | undefined => {
  if (isAxiosError(error)) {
    const data = error.response?.data
    return data?.message ?? data?.detail
  }

  if (error instanceof Error) {
    return error.message
  }

  return undefined
}

/**
 * Invalidates the orders cache to force a refresh of order data.
 * Called when a checksum error occurs, indicating the local data is out of sync.
 *
 * @param orderId - Optional specific order ID to invalidate
 */
export const invalidateOrdersCache = async (orderId?: string): Promise<void> => {
  // Invalidate specific order if provided
  if (orderId) {
    await hubQueryClient.invalidateQueries(['order', orderId])
  }

  // Invalidate order list queries
  await hubQueryClient.invalidateQueries(['orders'])
  await hubQueryClient.invalidateQueries(['orderChangeOptions'])
  await hubQueryClient.invalidateQueries(['orderCancellationOptions'])
  await hubQueryClient.invalidateQueries(['orderChangeRequestStatus'])
}
